/**
 * RequireAuth — autenticazione Phase 5 (IMPL-P5-006).
 *
 * Route guard: renders `children` only when the user is authenticated and
 * (optionally) holds one of the allowed `roles`. Otherwise redirects to
 * `/accedi?redirectTo=<original-path>` so the LoginForm can bounce back
 * after successful sign-in.
 *
 * The "loading" state renders a minimal SR-friendly placeholder rather than
 * a spinner library — the SPA stays small and the placeholder is replaced
 * within a single round-trip in 99% of cases.
 */
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthSession } from '../hooks/useAuthSession';
import type { Role } from '../lib/auth-client';

interface RequireAuthProps {
  children: ReactNode;
  /**
   * Optional role allow-list. When omitted any authenticated user is allowed.
   */
  roles?: Role[];
}

export function RequireAuth({ children, roles }: RequireAuthProps) {
  const { user, status } = useAuthSession();
  const location = useLocation();

  if (status === 'loading') {
    // Minimal SR-friendly placeholder. Avoids a flash of redirect during the
    // initial /me fetch, which would otherwise log the user out on every
    // hard refresh of a protected route.
    return (
      <main
        role="status"
        aria-live="polite"
        className="min-h-screen bg-background text-foreground flex items-center justify-center p-6"
      >
        <p className="text-sm text-muted-foreground">Caricamento in corso…</p>
      </main>
    );
  }

  if (status === 'unauthenticated' || !user) {
    const redirectTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/accedi?redirectTo=${redirectTo}`} replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
